
import { ref, get } from 'firebase/database';
import { database } from '../firebase/config';

let cachedVisitorCount = null;
let visitorCountCacheTime = null;
let ongoingVisitorRequest = null;
const VISITOR_CACHE_DURATION = 5 * 60 * 1000;

export async function getTotalVisitorCount() {
  if (cachedVisitorCount !== null && visitorCountCacheTime &&
      (Date.now() - visitorCountCacheTime) < VISITOR_CACHE_DURATION) {
    return cachedVisitorCount;
  }

  if (ongoingVisitorRequest) {
    return await ongoingVisitorRequest;
  }

  ongoingVisitorRequest = (async () => {
    try {
      const visitorRef = ref(database, '/stats/totalVisitorCount');
      const snapshot = await get(visitorRef);
      
      if (snapshot.exists()) {
        const count = snapshot.val();
        cachedVisitorCount = count;
        visitorCountCacheTime = Date.now();
        return count;
      } else {
        throw new Error('Visitor count not found in database');
      }
    } catch (error) {
      console.error('Error fetching visitor count from Realtime Database:', error);
      
      return getLocalFallbackCount();
    } finally {
      ongoingVisitorRequest = null;
    }
  })();

  return await ongoingVisitorRequest;
}

export async function getVisitorAnalytics(daysBack = 30) {
  try {
    const visitorRef = ref(database, '/stats/totalVisitorCount');
    const snapshot = await get(visitorRef);
    
    if (snapshot.exists()) {
      const totalVisitorCount = snapshot.val();
      return {
        totalUsers: totalVisitorCount || 0,
        activeUsers: Math.floor((totalVisitorCount || 0) * 0.1),
        newUsers: Math.floor((totalVisitorCount || 0) * 0.05),
        sessions: Math.floor((totalVisitorCount || 0) * 1.2),
        pageViews: Math.floor((totalVisitorCount || 0) * 2.5),
        countries: []
      };
    } else {
      throw new Error('Analytics data not found in database');
    }
  } catch (error) {
    console.error('Error fetching analytics data from Realtime Database:', error);
    return {
      totalUsers: 0,
      activeUsers: 0,
      newUsers: 0,
      sessions: 0,
      pageViews: 0,
      countries: []
    };
  }
}

function getLocalFallbackCount() {
  const VISITOR_KEY = 'fab_visitor_count';
  const LAST_VISIT_KEY = 'fab_last_visit';
  const SESSION_KEY = 'fab_session_id';
  
  const now = Date.now();
  const lastVisit = localStorage.getItem(LAST_VISIT_KEY);
  const currentSession = sessionStorage.getItem(SESSION_KEY);
  
  const isNewSession = !currentSession || !lastVisit || (now - parseInt(lastVisit)) > (30 * 60 * 1000);
  
  if (isNewSession) {
    const sessionId = `${now}_${Math.random().toString(36).substring(2, 11)}`;
    sessionStorage.setItem(SESSION_KEY, sessionId);
    
    let visitorCount = parseInt(localStorage.getItem(VISITOR_KEY) || '0');
    visitorCount += 1;
    localStorage.setItem(VISITOR_KEY, visitorCount.toString());
    localStorage.setItem(LAST_VISIT_KEY, now.toString());
    
    return visitorCount;
  }
  
  return parseInt(localStorage.getItem(VISITOR_KEY) || '0');
}

export function clearVisitorCache() {
  cachedVisitorCount = null;
  visitorCountCacheTime = null;
  ongoingVisitorRequest = null;
}

const visitorService = {
  getTotalVisitorCount,
  getVisitorAnalytics,
  clearVisitorCache
};

export default visitorService;